import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from '../BaseComponent';
import { ViewSettingsStore } from '../../stores/ViewSettingsStore';
import { LayoutType } from '../../constants/LayoutConstants';
import { HamburgerTab, SidebarModule, PopupMenu, Overlay } from '../../constants/Enums';
import { SidebarPosition, PersistentStore } from '../../stores/PersistentStore';
import { SceneStore } from '../../stores/SceneStore';
import { VisibilityStore } from '../../stores/VisibilityStore';
import { PconLoginStore } from '../../stores/PconLoginStore';
import { AppConfigurationStore } from '../../stores/AppConfigurationStore';
import { SceneActions } from '../../actions/SceneActions';
import { CookieBanner } from '../CookieBanner';
import { CompleteLock } from '../CompleteLock';
import { ConnectionError } from '../ConnectionError';
import { OverlaySelectBox } from '../OverlaySelectBox';
import { HamburgerButton } from '../HamburgerButton';
import { InputDialog } from '../InputDialog';
import { LengthInputDialog } from '../LengthInputDialog';
import { MessageBox } from '../MessageBox';
import { MinMaxButton } from '../MinMaxButton';
import { FloatingToolbar } from '../FloatingToolbar';
import { OverlayFrame } from '../overlay/OverlayFrame';
import { ViewerContainer } from '../ViewerContainer';
import { Sidebar } from '../Sidebar';
import { SidebarTab } from '../sidebar/SidebarTab';
import { HamburgerMenu } from '../hamburgermenu/HamburgerMenu';
import { TopBar } from '../TopBar';
import { SmartExtraPopup } from '../popupmenu/SmartExtraPopup';
import { FloorPlanSetup } from '../overlay/floorplan/FloorPlanSetup';
import { SolidFloorSetup } from '../popupmenu/SolidFloorSetup';
import { OAPPopup } from '../popupmenu/OAP/OAPPopup';
import { OAPActionsPopup } from '../popupmenu/OAP/OAPActionsPopup';
import { ToolsSetup } from '../popupmenu/ToolsSetup';
import { EnvironmentFloorPlanSetup } from '../popupmenu/EnvironmentFloorPlanSetup';
import PageContent from './PageContent';
import PageContainer from './PageContainer';
import { GlobalHelper } from '../../utils/GlobalHelper';
import { PriceInfo } from '../PriceInfo';

interface PageContainerState {
    hideBars: boolean;
    layoutType: LayoutType;
    popupMenu: PopupMenu;
    sidebarPosition: SidebarPosition;
    sidebar: SidebarModule;
    hamburgerTab: HamburgerTab | null;
    language: string;
    elementsInView: boolean;
    allowKeyboardInput: boolean;
}

export class Page extends BaseComponent<{}, PageContainerState> {
    public componentWillMount(): void {
        this.addStoreListener(ViewSettingsStore, this.onChange);
        this.addStoreListener(VisibilityStore, this.onChange);
        this.addStoreListener(PersistentStore, this.onChange);
        this.addStoreListener(SceneStore, this.onChange);
        this.onChange();
    }

    public componentDidMount(): void {
        window.addEventListener('keyup', this.keyUp);
    }

    public componentWillUnmount(): void {
        window.removeEventListener('keyup', this.keyUp);
    }

    public render(): JSX.Element {
        const { noWebGL } = AppConfigurationStore.getState();
        window['_App'] = GlobalHelper
        if (noWebGL) {
            return (
                <div className="error-page">
                    <div className="error-icon"/>
                    <div className="error-header">{t('WebGL required')}</div>
                    <div className="error-text">{t('This application requires WebGL. Please use a WebGL-compatible Browser.')}</div>
                    <div className="error-text"><a href="http://get.webgl.org/" target="_blank">{t('Click here to get further information.')}</a></div>
                </div>
            );
        }

        return (
            <PageContainer>
                <ViewerContainer layoutType={this.state.layoutType} noBars={this.state.hideBars} popup={this.state.popupMenu}/>
                <PageContent>
                    <PriceInfo/>
                    <CookieBanner />
                    <OverlayFrame layoutType={this.state.layoutType}/>
                    <Sidebar layoutType={this.state.layoutType} sidebarOrientation={this.state.sidebarPosition}/>
                    <SidebarTab
                        layoutType={this.state.layoutType}
                        sidebarOrientation={this.state.sidebarPosition}
                        noBars={this.state.hideBars}
                        showPrices={PconLoginStore.pricesEnabled()}
                    />
                    <CompleteLock />
                    <ConnectionError sidebarOpen={this.state.sidebar !== SidebarModule.NONE}/>
                    <HamburgerMenu
                        layoutType={this.state.layoutType}
                        sidebarOrientation={this.state.sidebarPosition}
                        sidebar={this.state.sidebar}
                        hamburgerTab={this.state.hamburgerTab}
                        userLanguage={this.state.language}
                    />
                    <TopBar
                        layoutType={this.state.layoutType}
                        sidebarOrientation={this.state.sidebarPosition}
                        elementsInView={this.state.elementsInView}
                        sidebar={this.state.sidebar}
                        userLanguage={this.state.language}
                    />
                    <OverlaySelectBox sidebarOrientation={this.state.sidebarPosition} sidebar={this.state.sidebar}/>
                    <HamburgerButton layoutType={this.state.layoutType} sidebarOrientation={this.state.sidebarPosition}/>
                    {!this.state.hideBars && <SmartExtraPopup sidebarOrientation={this.state.sidebarPosition}/>}
                    <InputDialog />
                    <LengthInputDialog />
                    <MessageBox />
                    <MinMaxButton sidebarOrientation={this.state.sidebarPosition} layoutType={this.state.layoutType}/>
                    <FloatingToolbar key="toolbar-floating" elementsInView={this.state.elementsInView} layoutType={this.state.layoutType}/>
                    {this.state.popupMenu === PopupMenu.FLOORPLAN_SETUP && <FloorPlanSetup layoutType={this.state.layoutType}/>}
                    {this.state.popupMenu === PopupMenu.SOLIDFLOOR_SETUP && <SolidFloorSetup layoutType={this.state.layoutType}/>}
                    {this.state.popupMenu === PopupMenu.TOOLS_SETUP && <ToolsSetup layoutType={this.state.layoutType}/>}
                    {this.state.popupMenu === PopupMenu.ENVIRONMENT_FLOORPLAN_SETUP && <EnvironmentFloorPlanSetup layoutType={this.state.layoutType}/>}
                    <div style={{position: 'absolute', width: '0', height: '0', overflow: 'hidden'}} id="invisible-container">
                        <img src="" id="fp-image"/>
                    </div>
                    <OAPPopup />
                    <OAPActionsPopup />
                </PageContent>
            </PageContainer>
        );
    }

    protected onChange = () => {
        const { hideBars, layoutType, hamburgerTab, sidebar } = ViewSettingsStore.getState();
        const { overlay, dialog, popupMenu } = VisibilityStore.getState();
        const { sidebarPosition, language } = PersistentStore.getState();
        this.setState({
            layoutType,
            hamburgerTab,
            hideBars,
            sidebarPosition,
            elementsInView: SceneStore.getState().elementCount > 0,
            sidebar,
            allowKeyboardInput: (
                (sidebar == null || sidebar === SidebarModule.NONE) &&
                (overlay == null || overlay === Overlay.NONE) &&
                dialog == null &&
                hamburgerTab == null
            ),
            language,
            popupMenu,
        });
    };

    protected keyUp = (event: KeyboardEvent): void => {
        if (this.state.allowKeyboardInput) {
            switch (event.key) {
                case 'Delete': // Delete
                    SceneActions.deleteSelectedElements();
                    break;
                case 'Insert': // Insert
                    if (event.shiftKey) {
                        SceneActions.cloneSavedElements();
                    }
                    break;
                case 'c': // c
                    if (event.ctrlKey) {
                        SceneActions.saveSelection();
                    }
                    break;
                case 'v': // v
                    if (event.ctrlKey) {
                        SceneActions.cloneSavedElements();
                    }
                    break;
                default:
                    break;
            }
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/mainContainer/Page.tsx